import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { getPublicStats } from '../api/client';
import { PropertyCard } from '../components/property/PropertyCard';
import { LocationSearch } from '../components/common/LocationSearch';
import { EmailSubscriptionPopup } from '../components/subscription';
import { REGIONS, CATEGORIES, PROPERTY_IMAGES } from '../data/mockData';
import { AdType, AdKind, PropertyCategory } from '../types';

export function HomePage() {
  const navigate = useNavigate();
  const { properties, demands, auctions, setFilters, clearFilters, propertiesLoaded, propertiesLoading, loadPropertiesFromApi } = useAppStore();
  const { t, language } = useI18n();
  
  // Load properties from API if not loaded
  useEffect(() => {
    if (!propertiesLoaded && !propertiesLoading) {
      loadPropertiesFromApi();
    }
  }, [propertiesLoaded, propertiesLoading, loadPropertiesFromApi]);
  
  const [siteStats, setSiteStats] = useState<{ users: number; activeListings: number } | null>(null);

  useEffect(() => {
    getPublicStats().then(setSiteStats).catch(() => {});
  }, []);

  const [activeTab, setActiveTab] = useState<AdKind>('Ponuda');
  const [searchForm, setSearchForm] = useState({
    adType: 'Prodaja' as AdType,
    category: '' as string,
    region: '' as string,
    priceMax: '' as string,
  });

  // Most popular offers - sorted by views (highest first), then urgent first
  const popularOffers = useMemo(() => {
    return [...properties]
      .filter(p => p.status === 'active')
      .sort((a, b) => {
        // Urgent first
        if (a.isUrgent && !b.isUrgent) return -1;
        if (!a.isUrgent && b.isUrgent) return 1;
        // Then by views
        return (b.views || 0) - (a.views || 0);
      })
      .slice(0, 4);
  }, [properties]);
  
  // Povoljno offers - affordable properties based on ad type rules
  const povoljnoOffers = useMemo(() => {
    return [...properties]
      .filter(p => {
        if (p.status !== 'active') return false;
        if (p.adType === 'Izdavanje') {
          // Za izdavanje: mesečna cena ispod 200€
          return p.price > 0 && p.price < 200;
        }
        if (p.adType === 'Prodaja') {
          if (p.category === 'Plac') {
            // Samo za građevinsko zemljište na prodaju
            if (p.subcategory !== 'Građevinsko zemljište') return false;
            if (!p.squareMeters || p.squareMeters <= 0) return false;
            const pricePerAr = (p.price / p.squareMeters) * 100;
            return pricePerAr < 1000;
          }
          // Ostale nekretnine na prodaju: cena po m² < 1500€
          if (!p.squareMeters || p.squareMeters <= 0) return false;
          return (p.price / p.squareMeters) < 1500;
        }
        return false;
      })
      .sort((a, b) => {
        // Urgent first
        if (a.isUrgent && !b.isUrgent) return -1;
        if (!a.isUrgent && b.isUrgent) return 1;
        // Then by price per m² (lowest first = most affordable)
        const priceA = a.price / (a.squareMeters || 1);
        const priceB = b.price / (b.squareMeters || 1);
        return priceA - priceB;
      })
      .slice(0, 4);
  }, [properties]);
  
  // Most popular demands - sorted by views
  const popularDemands = useMemo(() => {
    return [...demands]
      .filter(d => d.status === 'active')
      .sort((a, b) => {
        if (a.isUrgent && !b.isUrgent) return -1;
        if (!a.isUrgent && b.isUrgent) return 1;
        return (b.views || 0) - (a.views || 0);
      })
      .slice(0, 4);
  }, [demands]);
  
  // Most popular auctions - sorted by views
  const popularAuctions = useMemo(() => {
    return [...auctions]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 4);
  }, [auctions]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    clearFilters();
    setFilters({
      adKind: activeTab,
      adType: searchForm.adType as AdType,
      category: searchForm.category as PropertyCategory || undefined,
      region: searchForm.region || undefined,
      priceMax: Number(searchForm.priceMax) || undefined,
    });
    if (activeTab === 'Ponuda') {
      navigate('/ponuda');
    } else if (activeTab === 'Potražnja') {
      navigate('/potraznja');
    } else {
      navigate('/aukcije');
    }
  };

  // Helper to translate category names
  const getCategoryLabel = (name: string) => {
    if (language === 'en') {
      const categoryMap: Record<string, string> = {
        'Kuća': 'House',
        'Stan': 'Apartment',
        'Poslovni prostor': 'Commercial',
        'Plac': 'Land',
        'Garaža': 'Garage',
      };
      return categoryMap[name] || name;
    }
    return name;
  };

  const activeListingsCount = siteStats?.activeListings ?? 
    (properties.filter(p => p.status === 'active').length 
    + demands.filter(d => d.status === 'active').length 
    + auctions.length);

  const userCount = siteStats?.users ?? 0;

  const stats = [
    { label: language === 'sr' ? 'Aktivnih oglasa' : 'Active Listings', value: String(activeListingsCount) },
    { label: language === 'sr' ? 'Registrovanih korisnika' : 'Registered Users', value: userCount > 0 ? String(userCount) : '-' },
    { label: language === 'sr' ? 'Uspešnih transakcija' : 'Successful Transactions', value: language === 'sr' ? 'Stotine' : 'Hundreds' },
    { label: language === 'sr' ? 'Godina iskustva' : 'Years of Experience', value: '20+' },
  ];
  
  const tabOptions: { value: AdKind; label: string }[] = [
    { value: 'Ponuda', label: language === 'sr' ? 'Ponuda' : 'Listings' },
    { value: 'Potražnja', label: language === 'sr' ? 'Potražnja' : 'Wanted' },
    { value: 'Aukcija', label: language === 'sr' ? 'Aukcije' : 'Auctions' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Hero Section */}
      <section className="relative min-h-[600px] md:h-[600px] flex items-center justify-center overflow-visible z-10 pb-8 md:pb-0">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={PROPERTY_IMAGES[0]}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20 sm:pt-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {t.home.heroTitle} <span className="text-amber-500">{t.home.heroTitleHighlight}</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {t.home.heroSubtitle}
            </p>
          </motion.div>

          {/* Search Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSearch}
            className="relative z-20 bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 max-w-4xl mx-auto w-full"
          >
            {/* Tab Selection - Ponuda/Potražnja/Aukcije */}
            <div className="flex flex-wrap gap-2 mb-4 pb-2 justify-center sm:justify-start">
              {tabOptions.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                    activeTab === tab.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Ad Type - for Ponuda, Potražnja and Aukcije */}
            {(activeTab === 'Ponuda' || activeTab === 'Potražnja' || activeTab === 'Aukcija') && (
              <div className="flex flex-wrap gap-2 mb-4 justify-center sm:justify-start">
                {(activeTab === 'Potražnja' ? [
                  { value: 'Prodaja', label: language === 'sr' ? 'Kupovina' : 'Buying' },
                  { value: 'Izdavanje', label: language === 'sr' ? 'Rentiranje' : 'Renting' }
                ] : [
                  { value: 'Prodaja', label: language === 'sr' ? 'Prodaja' : 'For Sale' },
                  { value: 'Izdavanje', label: language === 'sr' ? 'Izdavanje' : 'For Rent' }
                ]).map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSearchForm({ ...searchForm, adType: type.value as AdType })}
                    className={`px-4 py-2 rounded-full font-medium transition-colors text-sm sm:text-base ${
                      searchForm.adType === type.value
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={searchForm.category}
                onChange={(e) => setSearchForm({ ...searchForm, category: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{t.home.propertyType}</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat.name} value={cat.name}>{getCategoryLabel(cat.name)}</option>
                ))}
              </select>
              
              <div className="w-full">
                <LocationSearch 
                  value={searchForm.region}
                  onChange={(val) => setSearchForm({ ...searchForm, region: val })}
                  placeholder={t.home.location}
                />
              </div>
              
              <input
                type="number"
                min="0"
                placeholder={t.home.maxPrice}
                value={searchForm.priceMax}
                onChange={(e) => setSearchForm({ ...searchForm, priceMax: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              
              <button
                type="submit"
                className="w-full bg-amber-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {t.common.search}
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-0 bg-slate-900 dark:bg-slate-950 py-8 mt-4 md:mt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-amber-500 mb-1">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Offers - Most Viewed */}
      {popularOffers.length > 0 && (
        <section className="py-16 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  🔥 {language === 'sr' ? 'Najpopularnije Ponude' : 'Most Popular Offers'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Nekretnine sa najviše pregleda' : 'Properties with the most views'}
                </p>
              </div>
              <Link
                to="/ponuda"
                className="hidden sm:flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
              >
                {t.home.viewAll}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularOffers.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Povoljno Offers - Best Price per m² */}
      {povoljnoOffers.length > 0 && (
        <section className="py-16 bg-emerald-50 dark:bg-emerald-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  💰 {language === 'sr' ? 'Povoljne Nekretnine' : 'Affordable Properties'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Najbolja cena po kvadratu' : 'Best price per square meter'}
                </p>
              </div>
              <Link
                to="/ponuda"
                className="hidden sm:flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t.home.viewAll}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {povoljnoOffers.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular Demands */}
      {popularDemands.length > 0 && (
        <section className="py-16 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  {language === 'sr' ? 'Najpopularnija Potražnja' : 'Most Popular Demands'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Šta naši korisnici najviše traže' : 'What our users are looking for'}
                </p>
              </div>
              <Link
                to="/potraznja"
                className="hidden sm:flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
              >
                {t.home.viewAll}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularDemands.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular Auctions */}
      {popularAuctions.length > 0 && (
        <section className="py-16 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  {language === 'sr' ? 'Najpopularnije Aukcije' : 'Most Popular Auctions'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Aukcije sa najviše interesovanja' : 'Auctions with most interest'}
                </p>
              </div>
              <Link
                to="/aukcije"
                className="hidden sm:flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
              >
                {language === 'sr' ? 'Sve aukcije' : 'All Auctions'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularAuctions.map((auction) => (
                <PropertyCard key={auction.id} property={auction} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {language === 'sr' ? 'Pretražite po kategorijama' : 'Browse by Category'}
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {language === 'sr' ? 'Pronađite tačno ono što tražite' : 'Find exactly what you\'re looking for'}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((category, index) => (
              <motion.button
                key={category.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => {
                  setFilters({ category: category.name as PropertyCategory });
                  navigate('/ponuda');
                }}
                className="bg-slate-50 dark:bg-slate-700 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow text-center group"
              >
                <div className="w-12 h-12 mx-auto mb-3 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <svg className="w-6 h-6 text-amber-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white">{getCategoryLabel(category.name)}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {category.subcategories.length} {language === 'sr' ? 'podkategorija' : 'subcategories'}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {language === 'sr' ? 'Imate nekretninu za prodaju ili izdavanje?' : 'Have a property to sell or rent?'}
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              {language === 'sr' ? 'Objavite oglas besplatno i dosegnite hiljade potencijalnih kupaca' : 'Post your listing for free and reach thousands of potential buyers'}
            </p>
            <Link
              to="/novi-oglas"
              className="inline-block bg-amber-500 text-white font-bold py-4 px-8 rounded-full hover:bg-amber-600 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              {t.common.postAd}
            </Link>
          </motion.div>
        </div>
      </section>

      <EmailSubscriptionPopup />
    </div>
  );
}
